<template>

  <div class="card pt-0 h-100">
    <div class="card-header d-flex mb-4">
      <!-- Case à cocher pour Scolarité -->
      <div class="form-check">
        <input type="radio" id="scolarite" value="scolarite" v-model="activeTab" class="form-check-input">
        {{ $t("todolist.tuition") }}
      </div>

      <!-- Case à cocher pour Autres Paiements -->
      <div class="form-check ms-3">
        <input type="radio" id="autre" value="autre" v-model="activeTab" class="form-check-input">
        {{ $t("todolist.other_payment") }}
      </div>
    </div>

    <spinner class="text-center mt-8 mb-8" v-if="loading" style="height: 200px">
      <div>
        <span class="mt-4">
          <Spinner></Spinner>
        </span>
      </div>
    </spinner>

    <div class="card-body pt-0" v-else-if="totalDue">
      <!-- Section Scolarité -->
      <div v-if="activeTab === 'scolarite'">
        <div class="content">
          <div class="titre" style="transform: translateY(-50px)">
            <h6>{{ $t("students_detail.school") }}</h6>
          </div>
          <ul class="list-group list-group-flush" data-toggle="checklist" style="transform: translateY(-50px)">
            <li class="checklist-entry list-group-item px-0"></li>
            <li v-for="(student, index) in tuitionPayments" :key="index"
              class="checklist-entry list-group-item px-0" @click="toggleDetails(index)" style="cursor: pointer;">
              <div class="checklist-item checklist-item-warning py-2 d-flex">
                <div class="checklist-info d-flex align-items-center">
                  <i class="bi bi-chevron-down me-2" :class="{ 'bi-chevron-up': activeIndex === index }"></i>
                  <h6 class="checklist-title mb-0" style="font-size: 14px;"> {{ $t("todolist.instalment") }}</h6>
                  <small class="text-xL ms-3"><span style="font-size:12px"> {{currency_code }} </span>{{ Number(student.amount).toLocaleString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          }) }}  </small>
                </div>
                <div class="form-check my-auto ms-auto text-end">
                  <span v-if="!student.status"> -----------</span>
                  <span class="badge badge-sm bg-gradient-warning" v-if="student.status == 'Pending'">
                    - {{ student.amount - student.splits_total_amount }}
                  </span>
                  <span class="badge badge-sm bg-gradient-success text-center" v-if="student.status == 'Settled'">
                    {{ $t("students_detail.settled") }}
                  </span>
                </div>
              </div>
              <div v-if="activeIndex === index" class="details px-4">
                <div class="payment" v-if="student.splits.length > 0">
                  <!--
                    Une ligne = un versement encaissé, montant seul. Le reçu ne
                    se délivre plus versement par versement : un seul document
                    récapitule toute la scolarité, sous la liste.
                  -->
                  <div class="row split-row" v-for="(split, splitIndex) in student.splits" :key="splitIndex">
                    <div class="col-md-6 installment"><i class="me-1 bi bi-arrow-right"></i> {{ $t("todolist.payment") }}</div>
                    <div class="col-md-6 text-end split-amount">
                      <span style="font-size:12px"> {{currency_code }} </span>
                      {{ Number(split.amount).toLocaleString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          }) }}
                    </div>
                  </div>
                </div>

                <div class="payment" v-else-if="isSettled(student)">
                  <div class="row split-row">
                    <div class="col-md-6 installment">
                      <i class="me-1 bi bi-check2"></i> {{ $t("todolist.payment_settled") }}
                    </div>
                    <div class="col-md-6 text-end split-amount">
                      <span style="font-size:12px"> {{ currency_code }} </span>
                      {{ Number(student.amount).toLocaleString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          }) }}
                    </div>
                  </div>
                </div>
                <div class="no-payment text-center py-3" v-else>
                  <p class="border py-2">{{ $t("todolist.no_payment") }}</p>
                </div>
              </div>
            </li>
            <li class="checklist-entry list-group-item px-0"></li>
          </ul>

          <!--
            Reçu de la scolarité : un seul document, qui porte la situation
            financière de l'élève sur l'ensemble des tranches qui lui sont
            appliquées — total dû, total versé, reste à payer.

            Proposé dès le premier versement, quel qu'en soit l'état : une
            famille qui a payé quelque chose a de quoi justifier, sans attendre
            que tout soit soldé.

            Le bouton s'affiche même sans modèle configuré : il mène alors à la
            configuration. Le masquer laissait l'utilisateur devant une fiche
            muette, sans indice de ce qui manque ni où le régler.
          -->
          <div class="text-center global-receipt" v-if="hasPayment">
            <button type="button" class="global-receipt-btn" @click="openGlobalReceipt">
              <i class="bi me-2" :class="receiptReady ? 'bi-download' : 'bi-sliders'"></i>
              {{
                receiptReady
                  ? $t("payment_receipt.download_global")
                  : $t("payment_receipt.configure_first")
              }}
            </button>
          </div>
        </div>
      </div>

      <!-- Section Autres Paiements -->
      <div v-if="activeTab === 'autre'">
        <div class="content">
          <div class="titre mb-4" style="transform: translateY(-50px)">
            <h6>{{ $t("todolist.other_payment") }}</h6>
          </div>
          <ul class="list-group list-group-flush" style="transform: translateY(-50px)">
            <li v-for="(student, index) in otherPayments" :key="index"
              class="checklist-entry list-group-item px-0" @click="toggleDetails(index)" style="cursor: pointer;
              border-top: 1px solid rgba(250, 250, 250, 0.3);
              border-bottom: 1px solid rgba(250, 250, 250, 0.3);
               ">
              <div class="checklist-item checklist-item-warning py-2 d-flex">
                <div class="checklist-info d-flex align-items-center">
                  <i class="bi bi-chevron-down me-2" :class="{ 'bi-chevron-up': activeIndex === index }"></i>
                  <h6 class="checklist-title mb-0" style="font-size: 14px;"> {{student.label}}</h6>
                  <small class="text-xL ms-3"><span style="font-size:12px"> {{currency_code }} </span>


                     {{ Number(student.amount).toLocaleString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          }) }}
                    </small>
                </div>
                <div class="form-check my-auto ms-auto text-end">
                  <span v-if="!student.status"> -----------</span>
                  <span class="badge badge-sm bg-gradient-warning" v-if="student.status == 'Pending'">
                    - {{ student.amount - student.splits_total_amount }}
                  </span>
                  <span class="badge badge-sm bg-gradient-success text-center" v-if="student.status == 'Settled'">
                    {{ $t("students_detail.settled") }}
                  </span>
                </div>
              </div>
              <div v-if="activeIndex === index" class="details px-4">
                <div class="payment" v-if="student.splits.length > 0">
                  <div class="row split-row" v-for="(split, splitIndex) in student.splits" :key="splitIndex">
                    <div class="col-md-6 installment"><i class="me-1 bi bi-arrow-right"></i> {{$t("todolist.payment") }}</div>
                    <div class="col-md-6 text-end split-amount">
                      <span style="font-size:12px"> {{currency_code }} </span>
                      {{ Number(split.amount).toLocaleString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          }) }}
                      <button
                        type="button"
                        class="split-receipt-btn"
                        :title="receiptReady
                          ? $t('payment_receipt.download_split')
                          : $t('payment_receipt.configure_first')"
                        @click.stop="openSplitReceipt(student, split, splitIndex)"
                      >
                        <i class="bi bi-receipt"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <!--
                  Tranche réglée en une fois : elle n'a aucun versement
                  détaillé, le règlement n'étant pas passé par le mécanisme de
                  paiement partiel. Afficher « Aucun paiement » sous une
                  tranche marquée « Réglé » se contredisait ; on montre la
                  ligne du règlement, avec son reçu comme pour un versement.
                -->
                <div class="payment" v-else-if="isSettled(student)">
                  <div class="row split-row">
                    <div class="col-md-6 installment">
                      <i class="me-1 bi bi-check2"></i> {{ $t("todolist.payment_settled") }}
                    </div>
                    <div class="col-md-6 text-end split-amount">
                      <span style="font-size:12px"> {{ currency_code }} </span>
                      {{ Number(student.amount).toLocaleString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          }) }}
                      <button
                        type="button"
                        style="font-size:20px;"
                        class="split-receipt-btn"
                        :title="receiptReady
                          ? $t('payment_receipt.download_split')
                          : $t('payment_receipt.configure_first')"
                        @click.stop="openSettledReceipt(student, index)"
                      >
                        <i class="bi bi-receipt"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="no-payment text-center py-3" v-else>
                  <p class="border py-2">{{ $t("todolist.no_payment") }}</p>
                </div>
              </div>
            </li>
            <li v-if="otherPayments.length === 0" class="checklist-entry list-group-item px-0">{{ $t("todolist.no_other_payment") }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="else" v-else>
      <div class="container text-center">
        <div class="text mt-3">
          <img class="image" src="@/assets/img/tranches.png" alt="" />
          <h5 class="mt-3">{{ $t("todolist.no_payment") }}</h5>
        </div>
      </div>
    </div>
    <div class="card-footer border-0 pt-3" v-if="$slots.footer && !loading">
      <slot name="footer"></slot>
    </div>
  </div>

  <!--
    Modèle pas encore configuré : on l'explique avant d'emmener l'utilisateur
    ailleurs. Une redirection immédiate au clic donnait l'impression d'avoir
    déclenché la mauvaise action.

    Téléportée dans <body> pour ne pas être rognée par la carte ni passer sous
    les autres éléments de la page.
  -->
  <teleport to="body">
    <div class="rc-setup-overlay" v-if="showSetupPrompt" @click.self="closeSetupPrompt">
      <div class="rc-setup-modal">
        <div class="rc-setup-icon">
          <i class="bi bi-receipt"></i>
        </div>
        <h6 class="mb-2 text-center">{{ $t("payment_receipt.setup_title") }}</h6>
        <p class="text-sm mb-0" style="opacity: 0.75">
          {{ $t("payment_receipt.setup_text") }}
        </p>
        <div class="d-flex justify-content-end gap-2 mt-4">
          <button type="button" class="rc-btn-ghost" @click="closeSetupPrompt">
            {{ $t("button.cancel") }}
          </button>
          <button type="button" class="rc-btn-confirm" @click="goToReceiptConfig">
            <i class="bi bi-sliders me-1"></i>{{ $t("payment_receipt.setup_action") }}
          </button>
        </div>
      </div>
    </div>
  </teleport>

  <!--
    Reçu de paiement de CET élève, résolu avec les données réelles de la
    tranche et du versement sélectionnés.
  -->
  <payment-receipt-generator
    :open="showReceipt"
    :student="studentDetails"
    :classroom="classroom"
    :payment="receiptPayment"
    :split="receiptSplit"
    :split-index="receiptSplitIndex"
    :payment-index="receiptPaymentIndex"
    :global="receiptGlobal"
    :payments="tuitionPayments"
    @close="showReceipt = false"
  />
</template>

<script>
import Spinner from "../components/Spinner.vue";
import PaymentReceiptGenerator from "@/views/Classroom/PaymentReceiptGenerator.vue";
import { isReceiptConfigured, loadReceiptFromApi } from "@/utils/classroomSetup";
import { hasTuitionPayment, isPaymentSettled } from "@/utils/receiptModel";
import api_config from "@/config";

export default {
  data() {
    return {
      loading: false,
      activeTab: 'scolarite', // Onglet par défaut
      activeIndex: null,
      currency_code: api_config.CURRENCY_CODE,
      /* Reçu en cours de consultation. */
      showReceipt: false,
      receiptPayment: null,
      receiptSplit: null,
      receiptSplitIndex: 0,
      receiptPaymentIndex: 0,
      receiptGlobal: false,
      /* Invite à configurer, affichée quand aucun modèle n'est enregistré. */
      showSetupPrompt: false,
    };
  },
  components: {
    Spinner,
    PaymentReceiptGenerator,
  },
  computed: {
    /** Tranches de scolarité, hors autres paiements. */
    tuitionPayments() {
      return (this.students || []).filter((payment) => payment && !payment.is_other_payment);
    },
    otherPayments() {
      return (this.students || []).filter((payment) => payment && payment.is_other_payment);
    },
    /**
     * Le reçu peut-il être délivré ?
     *
     * Uniquement si l'établissement a configuré son modèle : sans cela, le
     * document sortirait avec les valeurs par défaut plutôt qu'avec la mise en
     * forme choisie par l'école.
     */
    receiptReady() {
      return isReceiptConfigured();
    },
    /**
     * Un premier versement a-t-il été encaissé sur la scolarité ?
     *
     * C'est ce qui ouvre le reçu, et non plus le solde complet : le document
     * dit la situation du moment, il a donc un sens dès la première somme
     * reçue.
     */
    hasPayment() {
      return hasTuitionPayment(this.students);
    },
  },
  mounted() {
    // Interroge le serveur : sans cela, un utilisateur ouvrant l'application
    // depuis un autre poste ne verrait aucun bouton de reçu alors que son
    // école a déjà enregistré son modèle.
    loadReceiptFromApi();
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown);
    document.body.style.overflow = "";
  },
  watch: {
    // Empêche le défilement de l'arrière-plan pendant l'invite.
    showSetupPrompt(value) {
      document.body.style.overflow = value ? "hidden" : "";
    },
  },
  methods: {
    /** Échap ferme l'invite, comportement attendu sur ce type de fenêtre. */
    onKeydown(event) {
      if (event.key === "Escape" && this.showSetupPrompt) this.closeSetupPrompt();
    },
    toggleDetails(index) {
      this.activeIndex = this.activeIndex === index ? null : index;
    },
    closeSetupPrompt() {
      this.showSetupPrompt = false;
    },
    /**
     * Envoie configurer le modèle, en mémorisant l'écran de départ pour y
     * revenir une fois l'enregistrement fait.
     */
    goToReceiptConfig() {
      this.showSetupPrompt = false;
      const params = this.$route.params || {};
      const kept = {};
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          kept[`p_${key}`] = String(params[key]);
        }
      });
      this.$router.push({
        name: "payment-receipt-config",
        query: { from: this.$route.name, ...kept },
      });
    },
    /**
     * Reçu d'un versement précis.
     *
     * `paymentIndex` est le rang de la tranche dans la liste, transmis pour
     * que le reçu porte « Tranche 2 » plutôt qu'un « Tranche » indistinct
     * quand l'API ne renvoie pas le numéro.
     */
    openSplitReceipt(payment, split, splitIndex, paymentIndex = 0) {
      // Aucun modèle enregistré : on explique avant d'emmener configurer,
      // plutôt que d'ouvrir un document réglé par défaut que l'école n'a pas
      // choisi — ou de quitter la page sans prévenir.
      if (!this.receiptReady) {
        this.showSetupPrompt = true;
        return;
      }

      this.receiptPayment = payment;
      this.receiptSplit = split;
      this.receiptSplitIndex = splitIndex;
      this.receiptPaymentIndex = paymentIndex + 1;
      this.receiptGlobal = false;
      this.showReceipt = true;
    },
    /** La tranche est-elle intégralement réglée ? */
    isSettled(payment) {
      return isPaymentSettled(payment);
    },
    /**
     * Reçu d'une tranche réglée en une fois, sans versement détaillé.
     *
     * `split` reste nul : le reçu porte alors le total de la tranche, ce qui
     * est exactement ce qui a été encaissé.
     */
    openSettledReceipt(payment, paymentIndex = 0) {
      if (!this.receiptReady) {
        this.showSetupPrompt = true;
        return;
      }
      this.receiptPayment = payment;
      this.receiptSplit = null;
      this.receiptSplitIndex = 0;
      this.receiptPaymentIndex = paymentIndex + 1;
      this.receiptGlobal = false;
      this.showReceipt = true;
    },
    /** Reçu récapitulatif de toute la scolarité. */
    openGlobalReceipt() {
      if (!this.receiptReady) {
        this.showSetupPrompt = true;
        return;
      }

      this.receiptPayment = null;
      this.receiptSplit = null;
      this.receiptSplitIndex = 0;
      this.receiptPaymentIndex = 0;
      this.receiptGlobal = true;
      this.showReceipt = true;
    },
  },
  props: {
    totalDue: Number,
    students: Array,
    /** Élève complet : nom, matricule… imprimés sur le reçu. */
    studentDetails: { type: Object, default: () => ({}) },
    /** Classe de l'élève : { grade_code, speciality_code }. */
    classroom: { type: Object, default: () => ({}) },
  }
};
</script>

<style scoped>
.btn.active {
  background-color: #75acff;
  color: white;
}
.btn.inactive {
  background-color: transparent;
  color: white;
  border: 1px solid rgba(250, 250, 250, 0.3);
}

/* --- Reçu d'un versement ------------------------------------------------ */

.split-amount {
  /* Le bouton se pose après le montant sans décaler celui-ci : la colonne
     garde son alignement, qu'il soit visible ou non. */
  position: relative;
  white-space: nowrap;
}

.split-receipt-btn {
  border: none;
  background: transparent;
  color: #2ecebf;
  font-size: 14px;
  line-height: 1;
  padding: 2px 4px;
  margin-left: 8px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease;
}

/* Découvert au survol de la ligne — et gardé visible au clavier, sans quoi
   l'action serait inatteignable hors souris. */
.split-row:hover .split-receipt-btn,
.split-receipt-btn:focus-visible {
  opacity: 1;
}

.split-receipt-btn:hover {
  color: #6ee7db;
}

/* --- Invite à configurer le modèle -------------------------------------- */

.rc-setup-overlay {
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: rgba(8, 12, 24, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.rc-setup-modal {
  width: 100%;
  max-width: 460px;
  background: #141a2f;
  color: #e6ebf5;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  padding: 26px;
  box-shadow: 0 24px 60px -20px rgba(0, 0, 0, 0.7);
  text-align: left;
}

.rc-setup-icon {
  font-size: 30px;
  color: #2ecebf;
  margin-bottom: 10px;
  text-align: center;
}

.rc-btn-ghost {
  border-radius: 9px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: transparent;
  color: inherit;
  font-size: 13px;
  padding: 9px 16px;
  cursor: pointer;
}

.rc-btn-ghost:hover {
  border-color: rgba(255, 255, 255, 0.32);
}

.rc-btn-confirm {
  border: none;
  border-radius: 9px;
  background: #2ecebf;
  color: #06231f;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 18px;
  cursor: pointer;
}

.rc-btn-confirm:hover {
  opacity: 0.9;
}

/* --- Reçu global -------------------------------------------------------- */

.global-receipt {
  transform: translateY(-40px);
}

.global-receipt-btn {
  border: 1px solid rgba(46, 206, 191, 0.6);
  background: transparent;
  color: #2ecebf;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  padding: 11px 22px;
  width: 100%;
  cursor: pointer;
  transition: background 0.15s ease;
}

.global-receipt-btn:hover {
  background: rgba(46, 206, 191, 0.1);
}
</style>
